import { reach, setLocale } from 'yup'
import debounce from 'lodash.debounce'
import translate from 'src/intl/translate'
import { ifElse, map, pipe, join, curry } from 'ramda'
import { DATE_FORMAT } from 'src/ducks/consts'
import moment from 'moment'

setLocale({
  mixed: {
    required: 'validation.required'
  },
  string: {
    email: 'validation.email',
    min: ({ min }) => ({
      id: 'validation.minLength',
      values: { count: min }
    }),
    max: ({ max }) => ({
      id: 'validation.maxLength',
      values: { count: max }
    }),
    matches: 'validation.matches'
  },
  number: {
    min: ({ min }) => ({
      id: 'validation.min',
      values: { min }
    }),
    max: ({ max }) => ({
      id: 'validation.max',
      values: { max }
    })
  },
  date: {
    min: ({ min }) => ({
      id: 'validation.minDate',
      values: { min: moment(min).format(DATE_FORMAT) }
    }),
    max: ({ max }) => ({
      id: 'validation.maxDate',
      values: { max: moment(max).format(DATE_FORMAT) }
    })
  }
})

const translateErrors = errors =>
  errors.map(e => {
    if (typeof e === 'string') {
      return translate().formatMessage({ id: e })
    }
    if (typeof e === 'object') {
      return translate().formatMessage({ id: e.id }, e.values)
    }
  })

export const validate = (schema, context = {}) =>
  debounce((name, value, callback = () => {}) => {
    //Input name could be array in case of nested forms. If so, it must be
    //converted to a string with the correct path to the object
    const fieldName = ifElse(
      Array.isArray,
      pipe(
        map(v => (typeof v === 'number' ? `[${v}]` : v)),
        join('.')
      ),
      n => n
    )(name)

    return reach(schema, fieldName)
      .validate(value, { context })
      .then(() =>
        callback({
          valid: true,
          errors: []
        })
      )
      .catch(v =>
        callback({
          ...v,
          errors: translateErrors(v.errors)
        })
      )
  }, 200)

/**
 * validate with context - use this function for input validation with dependant
 * fields, pass all dependant fields as second argument
 * @param schema
 * @param context
 * @returns {debounced}
 */
export const validateWithContext = curry(validate)

export const validateDependantFields = schema =>
  debounce(
    (value, callback = () => {}) =>
      schema
        .validate(value)
        .then(() => callback({ valid: true, errors: [] }))
        .catch(v =>
          callback({
            ...v,
            errors: translateErrors(v.errors)
          })
        ),
    200
  )

export const validateSync =
  schema =>
  (name, value, callback = () => {}) =>
    reach(schema, name)
      .validate(value)
      .then(() => callback({ valid: true, errors: [] }))
      .catch(v =>
        callback({
          ...v,
          errors: translateErrors(v.errors)
        })
      )

export const validateSchema = (schema, context = {}) =>
  debounce(
    (values, callback) => schema.isValid(values, { context }).then(callback),
    400
  )

export const validateSchemaWithContext = curry(validateSchema)

export const validateSchemaSync = schema => (values, callback) =>
  schema.isValid(values).then(callback)
